import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "../hooks/useReveal";
import { CopyText } from "./CopyText";
import { fetchGalleryAlbums } from "../api/client";
import type { GalleryAlbumSummary } from "@tih/shared";

function formatEventDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function Gallery() {
  const ref = useReveal<HTMLElement>();
  const [albums, setAlbums] = useState<GalleryAlbumSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchGalleryAlbums()
      .then((data) => {
        if (!cancelled) setAlbums(data);
      })
      .catch((e) => {
        if (!cancelled) setError(String((e as Error).message ?? e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="section section-gallery" id="gallery" ref={ref}>
      <div className="gal-head">
        <div className="sec-eyebrow">
          <em>
            <CopyText path="gallery.eyebrow" as="span" />
          </em>
        </div>
        <h2 className="sec-title">
          <CopyText path="gallery.title" as="span" />
        </h2>
      </div>

      {loading ? (
        <div className="page-empty" aria-busy="true">
          Loading albums…
        </div>
      ) : error ? (
        <div className="page-empty page-empty--error" role="alert">
          {error}
        </div>
      ) : albums.length === 0 ? (
        <div className="page-empty">
          <p className="page-empty-title">Photo albums will appear here</p>
          <p>
            After each worship overnight we publish a quiet set of photos from the room. Nothing is
            listed yet — check back after the next night, or see{" "}
            <Link to="/event">the upcoming event</Link> for the date and place.
          </p>
        </div>
      ) : (
        <div className="gal-albums">
          {albums.map((album) => (
            <Link
              key={album.id}
              to={`/gallery/${album.slug}`}
              className="gal-album-card"
            >
              <div className="gal-album-cover">
                {album.coverUrl ? (
                  <img src={album.coverUrl} alt="" loading="lazy" />
                ) : (
                  <div className="photo-ph" data-label={album.title} />
                )}
                <span className="gal-album-count">
                  {album.photoCount} {album.photoCount === 1 ? "photo" : "photos"}
                </span>
              </div>
              <div className="gal-album-meta">
                <h3>{album.title}</h3>
                {album.eventDate && (
                  <time dateTime={album.eventDate}>{formatEventDate(album.eventDate)}</time>
                )}
                {album.caption && <p>{album.caption}</p>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
